/**
 * Feedback primitives: Badge, Spinner, Skeleton and EmptyState
 * (prompt.md §37, §40).
 *
 * WHY `Spinner` always carries a text label by default: a run stream that is
 * reconnecting must announce itself to assistive technology, not just spin.
 */

import type { CSSProperties, ReactNode } from "react";

import { chipStyle, MUTED_TEXT, stackStyle } from "../styles.ts";
import { toneColor, toneSurface, type StatusTone } from "../logic/runState.ts";

export interface BadgeProps {
  readonly tone?: StatusTone;
  readonly children: ReactNode;
  /** Optional trailing detail, e.g. a count. */
  readonly trailing?: ReactNode;
}

export function Badge({ tone = "neutral", children, trailing }: BadgeProps) {
  return (
    <span style={chipStyle(toneColor(tone), toneSurface(tone))}>
      {children}
      {trailing === undefined ? null : (
        <span style={{ color: "inherit", opacity: 0.75, fontVariantNumeric: "tabular-nums" }}>{trailing}</span>
      )}
    </span>
  );
}

export interface SpinnerProps {
  readonly size?: "sm" | "md" | "lg";
  /** `null` renders a decorative spinner; a string renders it as a status. */
  readonly label?: string | null;
}

const SPINNER_SIZES: Readonly<Record<NonNullable<SpinnerProps["size"]>, string>> = {
  sm: "12px",
  md: "16px",
  lg: "24px",
};

export function Spinner({ size = "md", label = "Working" }: SpinnerProps) {
  const dimension = SPINNER_SIZES[size];
  return (
    <span
      role={label === null ? "presentation" : "status"}
      aria-live={label === null ? undefined : "polite"}
      aria-label={label ?? undefined}
      style={{ display: "inline-flex", alignItems: "center", gap: "var(--dayvise-space-xs)" }}
    >
      <span
        aria-hidden="true"
        style={{
          width: dimension,
          height: dimension,
          borderRadius: "var(--dayvise-radius-pill)",
          border: "2px solid color-mix(in srgb, var(--dayvise-color-text-muted) 35%, transparent)",
          borderTopColor: "var(--dayvise-color-accent)",
          animation: "dayvise-spin var(--dayvise-motion-duration-slow) linear infinite",
        }}
      />
      {label === null ? null : <span style={MUTED_TEXT}>{label}</span>}
    </span>
  );
}

export interface SkeletonProps {
  readonly width?: string;
  readonly height?: string;
  readonly lines?: number;
  readonly style?: CSSProperties;
}

export function Skeleton({ width = "100%", height = "12px", lines = 1, style }: SkeletonProps) {
  const count = Math.max(1, lines);
  return (
    <span aria-hidden="true" style={{ display: "block", ...stackStyle("var(--dayvise-space-xs)"), ...style }}>
      {Array.from({ length: count }, (_unused, index) => (
        <span
          key={index}
          style={{
            display: "block",
            width: index === count - 1 && count > 1 ? "60%" : width,
            height,
            borderRadius: "var(--dayvise-radius-sm)",
            background: "linear-gradient(90deg, var(--dayvise-color-surface), var(--dayvise-color-raised), var(--dayvise-color-surface))",
            backgroundSize: "200% 100%",
            animation: "dayvise-shimmer calc(var(--dayvise-motion-duration-deliberate) * 3) var(--dayvise-motion-easing-linear) infinite",
          }}
        />
      ))}
    </span>
  );
}

export interface EmptyStateProps {
  readonly title: string;
  /** Explain what to do next, not just that the list is empty. */
  readonly description?: string;
  readonly action?: ReactNode;
  readonly icon?: ReactNode;
}

export function EmptyState({ title, description, action, icon }: EmptyStateProps) {
  return (
    <div style={{ ...stackStyle("var(--dayvise-space-sm)"), alignItems: "center", textAlign: "center", padding: "var(--dayvise-space-xl)" }}>
      {icon}
      <span style={{ color: "var(--dayvise-color-text-primary)", fontSize: "var(--dayvise-font-size-lg)", fontWeight: "var(--dayvise-font-weight-semibold)" }}>{title}</span>
      {description === undefined ? null : <span style={{ ...MUTED_TEXT, maxWidth: "48ch" }}>{description}</span>}
      {action}
    </div>
  );
}
